import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBranchContext } from "@/hooks/useBranchContext";

export interface PatientActivity {
  id: string;
  workflowStep: string;
  name: string;
  status: 'pending' | 'in-progress' | 'completed' | 'skipped';
  displayOrder: number;
  assignedTo?: string;
  completedBy?: string;
  timestamp?: string;
  startedAt?: string;
  assignedAt?: string;
  completionTime?: string;
  timeTakenMinutes?: number;
  patientId: string;
}

export const usePatientActivities = (patientId?: string) => {
  const { currentBranchId } = useBranchContext();
  return useQuery({
    queryKey: ["patient-activities", currentBranchId, patientId],
    queryFn: async () => {
      if (!patientId) return [];
      if (currentBranchId == null) return [];

      const { data, error } = await supabase
        .from("activities")
        .select(`
          id,
          workflow_step,
          name,
          status,
          display_order,
          assigned_to,
          completed_by,
          completion_time,
          timestamp,
          started_at,
          assigned_at,
          time_taken_minutes,
          patient_id
        `)
        .eq("patient_id", patientId)
        .eq("branch_id", currentBranchId)
        .neq("status", "deprecated")
        .in("workflow_step", ['MD', 'DSS', 'DSF', 'PC_PHARMACY', 'CS', 'BS', 'BA', 'PC', 'PD'])
        .order("display_order");

      if (error) throw error;

      return (data || []).map((activity: any) => {
        // Derive per-step duration:
        // 1) Prefer explicit time_taken_minutes from DB
        // 2) Fallback to (completion_time - started_at) in minutes when available
        let derivedTimeTakenMinutes: number | undefined = undefined;
        if (activity.time_taken_minutes != null && activity.time_taken_minutes > 0) {
          derivedTimeTakenMinutes = activity.time_taken_minutes;
        } else if (activity.started_at && activity.completion_time) {
          try {
            const start = new Date(activity.started_at);
            const end = new Date(activity.completion_time);
            const diffMs = end.getTime() - start.getTime();
            if (diffMs > 0) {
              derivedTimeTakenMinutes = Math.floor(diffMs / (1000 * 60));
            }
          } catch {
            // leave undefined on parse error
          }
        }

        return {
          id: activity.id,
          workflowStep: activity.workflow_step,
          name: activity.name,
          status: activity.status,
          displayOrder: activity.display_order,
          assignedTo: activity.assigned_to ?? undefined,
          completedBy: activity.completed_by ?? undefined,
          timestamp: activity.completion_time || activity.timestamp,
          startedAt: activity.started_at,
          assignedAt: activity.assigned_at,
          completionTime: activity.completion_time,
          timeTakenMinutes: derivedTimeTakenMinutes,
          patientId: activity.patient_id,
        } as PatientActivity;
      });
    },
    enabled: !!patientId && currentBranchId != null,
    refetchInterval: 5000,
  });
};
